import type { QueryClient } from "@tanstack/react-query";
import {
  Outlet,
  createRootRouteWithContext,
  redirect,
} from "@tanstack/react-router";
import React from "react";
import { Navigation } from "../../components/Navigation";
import { authClient } from "../lib/auth";

// Define the context we'll use with the router
export interface RouterContext {
  queryClient: QueryClient;
  auth: {
    user: {
      id: string;
      name: string;
      email: string;
      emailVerified: boolean;
      createdAt: Date;
      updatedAt: Date;
      image?: string | null;
    } | null;
    isAuthenticated: boolean;
  };
}

// Create the root route with our context type
export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootComponent,

  // Centralized authentication check at the root level
  beforeLoad: async ({ context, location }) => {
    // Check if user is authenticated using Better Auth
    try {
      const { data: session } = await authClient.getSession();

      // All routes require authentication - no public routes
      // The home page will handle showing login screen for unauthenticated users
      // But we don't need to redirect since every route will check auth state

      // Return auth state in context for all routes to use
      return {
        auth: {
          user: session?.user || null,
          isAuthenticated: !!session?.user,
        },
      };
    } catch (error) {
      // If error is a redirect, re-throw it
      if (error && typeof error === "object" && "href" in error) {
        throw error;
      }

      // For other errors, assume not authenticated
      console.error("Auth check failed:", error);
      return {
        auth: {
          user: null,
          isAuthenticated: false,
        },
      };
    }
  },

  // Global error boundary
  errorComponent: ({ error }: { error: unknown }) => {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="max-w-md w-full space-y-8">
          <div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Something went wrong
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              {error instanceof Error
                ? error.message
                : "An unexpected error occurred"}
            </p>
          </div>
        </div>
      </div>
    );
  },
});

function RootComponent() {
  const { auth } = Route.useRouteContext();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Only show navigation for authenticated users */}
      {auth.isAuthenticated && (
        <Navigation user={auth.user} isAuthenticated={auth.isAuthenticated} />
      )}
      <main className="flex-1">
        <Outlet />
      </main>
      {/* Dev tools (commented out but available) */}
      {/* <TanStackRouterDevtools position="bottom-right" /> */}
      {/* <ReactQueryDevtools /> */}
    </div>
  );
}

// NOTE - Only exported to avoid typescript errors during compilation when this is commented out
export const TanStackRouterDevtools: React.LazyExoticComponent<React.ComponentType> =
  React.lazy(() =>
    // Lazy load in development
    import("@tanstack/router-devtools").then((res) => ({
      default: res.TanStackRouterDevtools,
    })),
  );

// NOTE - Only exported to avoid typescript errors during compilation when this is commented out
export const ReactQueryDevtools: React.LazyExoticComponent<React.ComponentType> =
  React.lazy(() =>
    // Lazy load in development
    import("@tanstack/react-query-devtools/build/modern/production.js").then(
      (res) => ({
        default: res.ReactQueryDevtools,
      }),
    ),
  );
